import { Service } from "@feathersjs/feathers";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import client from "../feathers";
import { DataType, IData, UserRole } from '../types';
import { computedFn } from 'mobx-utils';



export abstract class Data<I extends IData> implements IData {
  /**
   * Note: Mobx requires default values to observe members
   */
  created_at?:   Date   = null;
  created_by?:   number = null;
  id?:           number = null;
  updated_at?:   Date   = null;
  updated_by?:   number = null;
  workspace_id:  number = null;

  protected service: Service<IData>;

  protected constructor(type: DataType, data?: IData) {
    // Since Mobx cannot track derived class objects automatically,
    // the decorators have to be provided manually.
    //
    // Members of derived classes have to be added manually in the same way!
    makeObservable(this, {
      created_at:   observable,
      created_by:   observable,
      id:           observable,
      updated_at:   observable,
      updated_by:   observable,
      workspace_id: observable,
      canEdit:      computed,
      isNew:        computed,
      save:         action,
      remove:       action,
      set:          action,
      patch:        action
    });

    // Get a reference to the feathers service for this type
    this.service = client.service(type as any);

    // Initialize data
    this.id           = data.id && Number(data.id);
    this.created_at   = data.created_at && new Date(data.created_at);
    this.updated_at   = data.updated_at && new Date(data.updated_at);
    this.updated_by   = data.updated_by && Number(data.updated_by);
    this.created_by   = data.created_by && Number(data.created_by);
    this.workspace_id = data.workspace_id && Number(data.workspace_id);
  }

  /***********************************************
   * Virtual Functions. They provide base functionality.
   * 
   * Overwrite these in derived classes!
   ************************************************/

  /**
    * Abstract Method! This is not supposed to be called. But since typescript does not
    * support abstract, static methods this is a workaround. 
    * 
    * Factory method to create a Data object either from data or with default values. 
    * 
    * Note: created_at/by and updated_at/by are set by the backend
    * and, thus, do not have to be added here. 
    */
  public static create(data?: IData): Data<IData> {
    return {} as Data<IData>;
  };

  /**
   * Dates and IDs are stored as strings and have to be converted to Date Objects and numbers 
   * respectively.
   */
  protected fromData(data: IData): void {  
    this.id           = data.id && Number(data.id);
    this.created_at   = data.created_at && new Date(data.created_at);
    this.updated_at   = data.updated_at && new Date(data.updated_at);
    this.updated_by   = data.updated_by && Number(data.updated_by);
    this.created_by   = data.created_by && Number(data.created_by);
    this.workspace_id = data.workspace_id && Number(data.workspace_id);
  }

  /**
   * Convert dates back to ISOStrings. 
   * 
   * Note: updated_by is set by the backend.
   */
  public toData(): any {
    return {
      id:           this.id,
      created_at:   this.created_at  && this.created_at.toISOString(),
      updated_at:   this.updated_at  && this.updated_at.toISOString(),
      created_by:   this.created_by,
      updated_by:   this.updated_by,
      workspace_id: this.workspace_id
    }
  }

  protected toPatchData(patch: Partial<I>): any {
    return {
      created_at:   "created_at" in patch ? patch.created_at  && patch.created_at.toISOString() : undefined,
      updated_at:   "updated_at" in patch ? patch.updated_at  && patch.updated_at.toISOString() : undefined,
      created_by:   "created_by" in patch ? patch.created_by  : undefined,
      updated_by:   "updated_by" in patch ? patch.updated_by  : undefined,
      workspace_id: "workspace_id" in patch ? patch.workspace_id : undefined,
    }
  }

  /**
   * @return <true> if the currently logged in user has the rights to edit this item
   */

   public get canEdit() : boolean {
    return true
  }

   public canEditField = computedFn((field?: keyof I): boolean => {
    return this.canEdit
  });

  /***********************************************
   * The following functions are generic and do not need to be overwritten.
   ************************************************/
  /**
   * @return <true> if the object is new. An item is locally created without ID. This is set and
   *          created when it is saved the first time. 
   */
  public get isNew(): boolean {
    return !Boolean(this.id);
  }

  /**
   * Saves the object to the database. This will trigger update/patch events that inturn 
   * will update the container of the corresponding store.
   * 
   * @param patch If provided, PATCH is used rather than UPDATE to store the data
   * @return Data returned by the called service (i.e. patch, update or created)
   */
  public async save(patch?: Partial<I>): Promise<IData> {
    try {
      if(this.id) {
        if(patch) return await this.service.patch( this.id, this.toPatchData(patch) ) as IData;
        else      return await this.service.update(this.id, this.toData()) as IData;
      } else {
        const newData: IData = await this.service.create({...this.toData(), id: undefined}) as IData;
        runInAction(() => this.fromData(newData)); // Add the new ID to the object. runInAction is need as this is an async call
        return newData;
      }
    } catch(error) {
      console.error("Failed to save object", this, error);
      throw error;
    }
  }

  /**
   * Removes the data object from the database
   */
  public remove = async (): Promise<boolean> => {
    try {
      await this.service.remove(this.id);
      return true;
    } catch(error) {
      console.error("Failed to remove object", error);
      return false;
    }
  }

  /**
   * Archives the object. Work in progress...
   */
  public archive = async (): Promise<void> => {
    console.error("Data cannot be archived");
  }

  /**
   * Change property value
   * 
   * @param prop  Property name to be set
   * @param value Value that @prop will be set to
   * @param save  If <true> value change will be patched to database
   */
  public async set(prop: keyof this, value: any, save: boolean = false): Promise<void> {
    if(this[prop] === value && !this.isNew) return;
    this[prop] = value;
    if(save) await this.save({[prop]: value} as I);
  }

  /**
   * Change property value
   * 
   * @param data  Data that should replace the current data
   * @param save  If <true> value change will be patched to database
   */
  public async patch(data: Partial<I>, save: boolean = false): Promise<void> {
    this.fromData(Object.assign(this, data));
    if(save) await this.save(data);
  }
}

